/**
 * BearerRefreshMonitor — global admin-only watcher.
 *
 * Polls the bearer-token status every 60s. When the most recent rotation
 * audit row has `ok: false` (and we haven't already warned about that exact
 * rotation ts), fires a persistent sonner toast so an on-call admin gets a
 * visual ping anywhere in the app — not just on the dashboard.
 *
 * Dedupe: we store the last-warned rotation timestamp in a ref so the same
 * failure doesn't re-toast on every poll. Recovery is silent.
 */
import { useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useApp } from '../../context/AppContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const POLL_MS = 60 * 1000;
const ADMIN_ROLES = new Set(['admin', 'sub_admin', 'business_head']);

export default function BearerRefreshMonitor() {
  const { currentUser } = useApp();
  const lastWarnedTsRef = useRef(null);

  useEffect(() => {
    if (!currentUser?.id) return undefined;
    if (!ADMIN_ROLES.has(currentUser.role)) return undefined;

    let cancelled = false;

    const check = async () => {
      try {
        const res = await axios.get(
          `${API}/admin/external-apis/bearer-token/status`,
          { params: { requester_id: currentUser.id } },
        );
        if (cancelled) return;
        const latest = (res.data?.recent_rotations || [])[0];
        if (!latest) return;
        if (latest.ok === false && latest.ts !== lastWarnedTsRef.current) {
          lastWarnedTsRef.current = latest.ts;
          toast.error(
            `Bearer-token rotation FAILED at ${new Date(latest.ts).toLocaleString()}${latest.source_email ? ` (via ${latest.source_email})` : ''}`,
            {
              id: `bearer-fail-${latest.ts}`,
              duration: Infinity,
              description: 'HiTeam auth is degraded. Open Admin → External APIs to force a rotate or check credentials.',
              action: {
                label: 'Dismiss',
                onClick: () => {},
              },
            },
          );
        }
        // Recovery: if latest is ok=true AND we had previously warned, clear
        // the warning flag so a future failure re-toasts.
        if (latest.ok === true && lastWarnedTsRef.current) {
          lastWarnedTsRef.current = null;
        }
      } catch (_) { /* silent — poll again later */ }
    };

    // Delay first check 10s after mount (let app finish loading) then poll.
    const initial = setTimeout(check, 10_000);
    const interval = setInterval(check, POLL_MS);
    return () => {
      cancelled = true;
      clearTimeout(initial);
      clearInterval(interval);
    };
  }, [currentUser]);

  return null;
}
